import { push } from 'connected-react-router';
import { call, put, takeEvery, select } from 'redux-saga/effects';

import { getScribeLink } from '../shared/helpers/linkHelper';
import {
  GET_PAYMENT_CARD_PARAMS_REQUESTED,
  getPaymentCardParamsFailure,
  getPaymentCardParamsSuccess,
  CREATE_NEW_CUSTOMER_ORDER_REQUESTED,
  createNewCustomerOrderFailure,
  createNewCustomerOrderSuccess,
  GET_EXISTING_PAYMENT_CARD_DETAILS_REQUESTED,
  getExistingPaymentMethodSuccess,
  getExistingPaymentMethodFailure,
  UPGRADE_EXISTING_ACCOUNT_REQUESTED,
  upgradeExistingAccountSuccess,
  upgradeExistingAccountFailure,
  GET_EXISTING_PAYMENT_CARD_DETAILS_SUCCESS,
  getPaymentCardParams as getPaymentCardParamsAction
} from '../actionCreators/paymentActions';
import { COUNTRY_UPDATE } from '../actionCreators/locationActions';
import { authSuccess, validateTokenSuccess } from '../actionCreators/authActions';
import { authenticationProvider } from '../shared/providers/AuthenticationProvider';
import {
  getPaymentCardParams,
  createNewCustomerOrder,
  getExistingPaymentMethod,
  upgradeExistingAccountMethod
} from '../shared/providers/PaymentProvider';
import { ROUTE_CODES, SUCCESS } from '../config/routes';
import { VSP_100_MONTHLY } from '../config/licenceType';
import { sendErrorToSentry } from '../logging';

import { getScribeById } from './selectors';

function* handleGetPaymentCardParams() {
  try {
    const { currencyCode, existingPaymentDetails } = yield select(({ location, payment }) => ({
      currencyCode: location?.currency,
      existingPaymentDetails: payment?.existingPaymentDetails
    }));

    if (existingPaymentDetails) {
      return;
    }

    const params = yield call(getPaymentCardParams, currencyCode);
    yield put(getPaymentCardParamsSuccess(params));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getPaymentCardParamsFailure(e));
  }
}

function* handleGetPaymentMethod() {
  try {
    const cardDetails = yield call(getExistingPaymentMethod);
    yield put(getExistingPaymentMethodSuccess(cardDetails));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(getExistingPaymentMethodFailure(e));
  }
}

function* handleUpgradeExistingAccount() {
  try {
    const params = yield call(upgradeExistingAccountMethod, VSP_100_MONTHLY);
    yield put(upgradeExistingAccountSuccess(params));

    // Fetch fresh user details after upgrade
    const result = yield call(authenticationProvider.validateToken);
    yield put(validateTokenSuccess(result.user));
    yield put(authSuccess(result.user, result.token));
    yield put(push(SUCCESS));
  } catch (e) {
    sendErrorToSentry(e);
    yield put(upgradeExistingAccountFailure(e));
    yield put(push(ROUTE_CODES.FAILED));
  }
}

function* handleCreateNewCustomerOrder(action) {
  const { paymentRef, licenceType, scribeId } = action;
  const scribe = yield select(getScribeById, scribeId);

  try {
    const resp = yield call(createNewCustomerOrder, paymentRef, licenceType);

    if (resp.succeed) {
      yield put(createNewCustomerOrderSuccess(resp));
      const result = yield call(authenticationProvider.validateToken);
      yield put(validateTokenSuccess(result.user));
      yield put(authSuccess(result.user, result.token));
    } else {
      yield put(createNewCustomerOrderFailure());
      if (scribe) {
        yield put(push(getScribeLink(scribe, ROUTE_CODES.FAILED)));
      } else {
        yield put(push(ROUTE_CODES.FAILED));
      }
    }
  } catch (e) {
    sendErrorToSentry(e);
    yield put(createNewCustomerOrderFailure(e));
    if (scribe) {
      yield put(push(getScribeLink(scribe, ROUTE_CODES.FAILED)));
    } else {
      yield put(push(ROUTE_CODES.FAILED));
    }
  }
}

function* listenForPaymentCardSuccess(action) {
  const currency = yield select(({ location }) => location.currency);

  if (!action.payload && currency) {
    yield put(getPaymentCardParamsAction());
  }
}

function* listenForCountryChange(action) {
  yield put(getPaymentCardParamsAction());
}

function* paymentSagas() {
  yield takeEvery(GET_PAYMENT_CARD_PARAMS_REQUESTED, handleGetPaymentCardParams);
  yield takeEvery(CREATE_NEW_CUSTOMER_ORDER_REQUESTED, handleCreateNewCustomerOrder);
  yield takeEvery(GET_EXISTING_PAYMENT_CARD_DETAILS_REQUESTED, handleGetPaymentMethod);
  yield takeEvery(UPGRADE_EXISTING_ACCOUNT_REQUESTED, handleUpgradeExistingAccount);
  yield takeEvery(GET_EXISTING_PAYMENT_CARD_DETAILS_SUCCESS, listenForPaymentCardSuccess);
  yield takeEvery(COUNTRY_UPDATE, listenForCountryChange);
}

export default paymentSagas;
